import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Download, Upload, FileSpreadsheet } from "lucide-react";
import { useScope } from "@/hooks/use-scope";
import { useImplementationProgress } from "@/lib/implementation-progress";
import {
  TrackPageHeader,
  ChecklistStrip,
  SectionGrid,
  type TrackSection,
} from "@/components/implementation/track-page";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/app/implementation/workspace/data")({
  component: DataTrack,
});

function DataTrack() {
  const { tenantId } = useScope();
  const progress = useImplementationProgress(tenantId);
  const track = progress.tracks.data;

  const dataset = (
    id: string,
    name: string,
    description: string,
    template: string,
    live: string,
  ): TrackSection => ({
    id,
    name,
    eyebrow: "Dataset",
    description,
    status: "ready",
    actions: [
      { label: "Download template", href: template, icon: "download", variant: "secondary" },
      { label: "Manage", to: live, icon: "arrow", variant: "ghost" },
      {
        label: "Upload",
        to: "/app/implementation/launch-kit-preview",
        icon: "upload",
        variant: "primary",
        hint: "Runs the Launch Kit preview — no data is written",
      },
    ],
  });

  const sections: TrackSection[] = [
    dataset(
      "customers",
      "Customers",
      "For 3PLs, the merchants whose inventory you hold. For DTC, your brand itself.",
      "/launch-kit/customers-template.csv",
      "/app/admin/customers",
    ),
    dataset(
      "facilities",
      "Facilities",
      "The physical buildings you operate. Locations bind to a facility code from this tab.",
      "/launch-kit/facilities-template.csv",
      "/app/admin/facilities",
    ),
    dataset(
      "skus",
      "SKUs",
      "Item master with dimensions, weight, and barcode. Required to pick, pack, and ship.",
      "/launch-kit/skus-template.csv",
      "/app/inventory",
    ),
    dataset(
      "locations",
      "Locations",
      "The physical grid inside your facility. Aisle, bay, level, position, and location type.",
      "/launch-kit/locations-template.csv",
      "/app/admin/facilities",
    ),
    dataset(
      "users",
      "Users",
      "Everyone who logs into CairnOS — implementers, operators, portal users. Managed alongside Foundation.",
      "/launch-kit/users-template.csv",
      "/app/admin/users",
    ),
    dataset(
      "opening_inventory",
      "Opening inventory",
      "Starting on-hand snapshot for go-live. Loaded once locations and SKUs exist; reconciled during Validation.",
      "/launch-kit/opening-inventory-template.csv",
      "/app/inventory",
    ),
  ];

  return (
    <div>
      <TrackPageHeader
        track={track}
        eyebrow="Track 02 · Data"
        description="The state your warehouse's promises will defend. Every dataset has a template you can download today, a production surface where it lives, and — soon — an in-track upload and validation loop."
      />
      <ChecklistStrip track={track} />
      <LaunchKitBanner />
      <SectionGrid sections={sections} />
    </div>
  );
}

function LaunchKitBanner() {
  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-border bg-card shadow-[var(--shadow-card)]">
      <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-7">
        <div className="flex items-start gap-4">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
              Launch Kit · Primary artifact
            </div>
            <div className="mt-1 text-base font-medium text-foreground">
              CairnOS Launch Kit workbook
            </div>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              One Excel workbook — Customers, Facilities, Locations, SKUs, Users, and Opening Inventory —
              with built-in validation. Fill it in, upload it back, and CairnOS will run a full
              readiness preview before any data is written.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="default" size="sm" className="h-9">
            <a href="/launch-kit/CairnOS-Launch-Kit.xlsx" download>
              <Download className="mr-2 h-4 w-4" />
              Download workbook
            </a>
          </Button>
          <Button asChild variant="secondary" size="sm" className="h-9">
            <Link to="/app/implementation/launch-kit-preview">
              <Upload className="mr-2 h-4 w-4" />
              Upload for preview
            </Link>
          </Button>
        </div>
      </div>
      <div className="border-t border-border bg-muted/30 px-6 py-2.5 text-[11px] text-muted-foreground sm:px-7">
        Per-dataset CSV templates below remain as a fallback while the workbook flow is being proven.
        Preview never writes to your database.
      </div>
    </div>
  );
}